import React, { useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

import { calculatePrediction } from '../utils/prediction';
import { calculateProgressSeriesData } from '../utils/progressSeriesData';
import {
  sumEntriesByWork,
  sortEntries,
  getExtemumEntries,
  getMinX,
  getMaxX,
  getMaxY,
  getTotalSpentTime,
  getTotalSpentTimeFirstDay,
} from '../utils/common';
import GET_PROJECT from '../../../graphql/queries/getProject';
import { useManipulators } from '../../../hooks/useManipulators';
import GET_ENTRIES from '../../../graphql/queries/getEntries';
import { ENTRY_CREATED_IN_PROJECT } from '../../../graphql/subscriptions/entryCreatedInProject';

export const ProjectContext = React.createContext();

export const ProjectContextProvider = withRouter(({ children, match }) => {
  const { projectId } = match.params;

  const [chartReady, setChartReady] = useState(null);
  const [chartCalculating, setChartCalculating] = useState(false);
  const [budget, setBudget] = useState(null);
  const [minX, setMinX] = useState(null);
  const [maxX, setMaxX] = useState(null);
  const [maxY, setMaxY] = useState(null);
  const [progressSeriesData, setProgressSeriesData] = useState([]);
  const [totalSpentTime, setTotalSpentTime] = useState(null);
  const [totalSpentTimeByWork, setTotalSpentTimeByWork] = useState(null);
  const [predictedValue, setPredictedValue] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  // On-demand full history loading for chart
  const [showFullHistoryButton, setShowFullHistoryButton] = useState(true);

  const { filters, orderBy } = useManipulators({
    filters: [
      {
        name: 'start',
        type: 'input',
        default: '01.01.1970',
        hide: true,
      },
      {
        name: 'project',
        type: 'select',
        default: Number(projectId),
      },
    ],
    orderBy: {
      options: [
        { text: 'Start time', value: 'start' },
      ],
    },
  });

  const { data: projectData = {}, loading: projectLoading } = useQuery(GET_PROJECT, {
    variables: {
      projectId: Number(projectId),
    },
  });

  const { data: entriesData = {}, loading: entriesLoading, subscribeToMore } = useQuery(GET_ENTRIES, {
    fetchPolicy: 'no-cache', // no-cache prevents writing 30,000 entries to Apollo's InMemoryCache, which freezes the browser
    variables: {
      filters: {
        project: Number(projectId),
        start: '01.01.1970',
        end: moment().add(1, 'year').format('DD.MM.YYYY'),
      },
      orderBy,
    },
  });

  useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: ENTRY_CREATED_IN_PROJECT,
      variables: { projectId: Number(projectId) },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) {
          return prev;
        }

        const createdEntries = subscriptionData.data.entryCreatedInProject;
        const newEntries = createdEntries.filter((x) => prev.entries.findIndex((y) => y.id === x.id) === -1);

        return {
          entries: [
            ...newEntries.reverse(),
            ...prev.entries,
          ],
        };
      },
    });

    return () => unsubscribe();
  }, [subscribeToMore]);

  useEffect(() => {
    if (!entriesLoading && entriesData.entries && projectData.project) {
      setChartCalculating(true);
      setChartReady(false);

      // Defer heavy chart calculations to prevent UI blocking
      setTimeout(() => {
        const entries = sortEntries([...entriesData.entries]);
        const extemumEntries = getExtemumEntries(entries);

        const minXValue = getMinX(extemumEntries.earliestEntry, projectData.project.start);
        const maxXValue = getMaxX(projectData.project.end, extemumEntries.latestEntry);

        setMinX(minXValue);
        setMaxX(maxXValue);

        const seriesData = calculateProgressSeriesData(entries, minXValue);
        const spentTime = getTotalSpentTime(seriesData);
        const spentTimeFirstDay = getTotalSpentTimeFirstDay(seriesData)
        const prediction = calculatePrediction(spentTime, spentTimeFirstDay, minXValue, maxXValue)

        setProgressSeriesData(seriesData);
        setTotalSpentTime(spentTime);
        setPredictedValue(prediction);

        const projectBudget = Number(projectData.project.budget)
        setBudget(projectBudget);
        setMaxY(getMaxY(prediction, projectBudget));

        const summedEntriesByWork = sumEntriesByWork(entries);
        setTotalSpentTimeByWork(summedEntriesByWork);

        setChartCalculating(false);
        setChartReady(true);
      }, 0);
    }
  }, [entriesLoading, entriesData, projectData]);

  return (
    <ProjectContext.Provider
      value={{
        entriesData,
        entriesLoading,

        projectData,
        projectLoading,

        chartReady,
        chartCalculating,
        budget,
        totalSpentTime,
        totalSpentTimeByWork,
        progressSeriesData,
        predictedValue,

        boundaries: {
          minX,
          maxX,
          maxY,
        },

        selectedRows,
        setSelectedRows,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
});
